// =============================================================================
// domain/dashGauges.ts — the Dashboard's six arc gauges, and the equity series.
// -----------------------------------------------------------------------------
// WIN RATE · RISK-ADJUSTED WIN RATE · EXPECTANCY (R) · DISCIPLINE SCORE ·
// PROFIT FACTOR · TARGET EFFICIENCY.
//
// ── 🚨 EACH GAUGE HAS TWO NUMBERS, AND CONFLATING THEM IS THE BUG ──
//
// The VALUE is what the centre text prints — `0.29R`, `0.85`, `14%`. The ARC
// PERCENTAGE is where the needle sits, and for four of the six it is a DIFFERENT
// number on a different scale:
//
//   win rate            value 0–100  · arc = value
//   risk-adj win rate   value 0–100  · arc = value
//   expectancy (R)      value −∞..∞  · arc = (R / 3) × 100, clamped 0–100
//   profit factor       value 0..∞   · arc = (PF / 3) × 100, clamped; ∞ → full
//   target efficiency   value −∞..∞  · arc = the value, clamped 0–100
//   discipline          value 0–100  · arc = value
//
// So a `-0.4R` expectancy prints "-0.40R" and draws an EMPTY arc, and a 140%
// target efficiency prints "140%" and draws a FULL one. Returning a single
// number per gauge would force the UI to re-derive the other, which is where the
// two would drift apart.
//
// ⚠ `/ 3` twice is not a coincidence — the old client notes it uses the same
// scale for profit factor and expectancy-R "for visual consistency", so the two
// dials are readable against each other. Keep them equal or neither means
// anything relative to the other.
// =============================================================================

import type { PnlUnit } from './types';

/** What the gauges need from a trade. */
export interface GaugeTrade {
  netPnl?: number | null;
  rrRealized?: number | null;
  rrPlanned?: number | null;
  annotated?: boolean | null;
  /** The six breach flags share one jsonb column. */
  booleans?: Record<string, unknown> | null;
}

export interface Gauge {
  /** The centre text's underlying number. Null = no data, render `—`. */
  value: number | null;
  /** 0–100, already clamped. Where the arc stops. */
  arcPct: number;
  /** How many trades the value is over. */
  sample: number;
}

const clamp100 = (n: number) => Math.max(0, Math.min(100, n));

/**
 * Risk-adjusted win rate — the share of trades that made at least 1R.
 *
 * ⚠ The DENOMINATOR is trades with a realized R, not all trades. A trade with no
 * `rrRealized` has no risk basis, so it can neither pass nor fail this bar;
 * counting it as a failure would punish the trader for an unset stop rather than
 * for a bad outcome.
 *
 * ⚠ And the bar is `>= 1R`, not `> 0`. A scratch winner or a +0.4R win does NOT
 * qualify — that is the whole point of the metric sitting beside the ordinary
 * win rate.
 */
export function riskAdjustedWinRate(trades: readonly GaugeTrade[]): Gauge {
  const withR = trades.filter((t) => t.rrRealized != null);
  const big = withR.filter((t) => (t.rrRealized ?? 0) >= 1);
  const pct = withR.length ? (big.length / withR.length) * 100 : 0;
  return { value: withR.length ? pct : null, arcPct: clamp100(pct), sample: withR.length };
}

/**
 * Expectancy in R.
 *
 * ⚠ NOT `Kpis.avgRR`, and the old client says so explicitly: this averages over
 * trades that HAVE an `rrRealized`, treating the metric as "per trade you could
 * measure". They are different numbers and both appear on this screen.
 */
export function expectancyR(trades: readonly GaugeTrade[]): Gauge {
  const withR = trades.filter((t) => t.rrRealized != null);
  if (!withR.length) return { value: null, arcPct: 0, sample: 0 };
  const avg = withR.reduce((s, t) => s + (t.rrRealized ?? 0), 0) / withR.length;
  // ⚠ Negative expectancy draws an EMPTY arc while the text still reads "-0.40R".
  return { value: avg, arcPct: clamp100((avg / 3) * 100), sample: withR.length };
}

/**
 * Profit factor, mapped onto the same 0–3 scale as expectancy-R.
 *
 * ⚠ `Infinity` is a real state — winners and no losers — and it FILLS the arc
 * rather than blanking it. The centre text renders "∞"; the caller is
 * responsible for that, which is why `value` is returned as `Infinity` rather
 * than pre-formatted.
 */
export function profitFactorGauge(profitFactor: number, sample: number): Gauge {
  const raw = profitFactor === Infinity ? 3 : profitFactor;
  return {
    value: sample ? profitFactor : null,
    arcPct: clamp100((raw / 3) * 100),
    sample,
  };
}

/**
 * Target efficiency — `rrRealized / rrPlanned`, averaged.
 *
 * "Did you execute the plan you wrote?" 100% = took the full planned target,
 * 50% = scaled out early, 0% = scratched at breakeven, negative = stopped out
 * past your planned stop, over 100% = let a runner go.
 *
 * ⚠ Requires BOTH a planned R greater than zero AND a realized one. A trade with
 * no written plan cannot be judged against it, and treating a missing
 * `rrPlanned` as zero would divide by zero and produce Infinity on the one
 * metric whose whole job is accountability.
 *
 * ⚠ The ARC clamps at 100 while the TEXT does not, so a 140% runner reads "140%"
 * on a full dial. Clamping the text too would hide the best executions on the
 * screen.
 */
export function targetEfficiency(trades: readonly GaugeTrade[]): Gauge {
  const usable = trades.filter(
    (t) => (t.rrPlanned ?? 0) > 0 && t.rrRealized != null,
  );
  if (!usable.length) return { value: null, arcPct: 0, sample: 0 };
  const avg = usable.reduce(
    (s, t) => s + ((t.rrRealized ?? 0) / (t.rrPlanned as number)) * 100, 0,
  ) / usable.length;
  return { value: avg, arcPct: clamp100(avg), sample: usable.length };
}

/** The six breach flags, in the old client's order. */
export const DISCIPLINE_BREACH_KEYS = [
  'planNotFollowed', 'stopNotHonored', 'targetNotHonored',
  'revengeTrade', 'fomoTrade', 'overtraded',
] as const;

/**
 * One trade's discipline grade, in `[-1, +1]`. Null when the trade was never
 * reviewed.
 *
 * ⚠ EXTRACTED SO THE AGGREGATE AND THE PER-TRADE COLUMN CANNOT DRIFT (#45).
 * Beta has exactly this pair — `tradeDisciplineGrade` and `dashDisciplineScore`
 * — and the Recent Trades table shows the per-trade figure in a `Disc.` column.
 * Two copies of `(n - 2 * bad) / n` in two files is how the gauge and the column
 * end up disagreeing about the same trade on the same screen.
 *
 * ⚠ `annotated === false` → NULL, never 0 and never 100. The breach flags are
 * stored as "absent means it did not happen", so an unreviewed import has six
 * absent flags and would otherwise read as a PERFECT grade with nothing judged.
 */
export function tradeDisciplineGrade(t: GaugeTrade | null | undefined): number | null {
  if (!t || t.annotated === false) return null;
  const b = t.booleans ?? {};
  let bad = 0;
  for (const k of DISCIPLINE_BREACH_KEYS) if (b[k] === true) bad++;
  const n = DISCIPLINE_BREACH_KEYS.length;
  return (n - 2 * bad) / n;
}

/** The same grade mapped onto 0–100 for display. −1 → 0%, 0 → 50%, +1 → 100%. */
export function tradeDisciplinePct(t: GaugeTrade | null | undefined): number | null {
  const g = tradeDisciplineGrade(t);
  return g == null ? null : (g + 1) * 50;
}

export interface DisciplineScore extends Gauge {
  /** Clear dimensions across all judged trades — the "good votes". */
  posTotal: number;
  /** Flagged breaches — the "bad votes". */
  negTotal: number;
}

/**
 * Discipline score.
 *
 * Every reviewed trade votes on six dimensions: a flagged breach is −1, a clear
 * one is +1. The grade is `(clear − flagged) / 6`, in `[-1, +1]`, and the gauge
 * maps that onto 0–100.
 *
 * ── ⚠ AN UNREVIEWED TRADE SCORES NOTHING, AND THAT ASYMMETRY IS LOAD-BEARING ──
 *
 * The flags are stored as "absent means it did not happen" (an unchecked box is
 * DELETED, never written as false — one of the three asymmetries this codebase
 * keeps tripping over). So a never-opened import has six absent flags, which
 * under the checkbox model reads as six clear dimensions and a PERFECT grade
 * with nothing actually judged.
 *
 * `annotated === false` therefore returns null and the trade is skipped
 * entirely. Importing 400 trades must not push discipline to 99%.
 *
 * ⚠ A single judged trade is enough. Discipline is a tally of what you did, not
 * a statistical estimate, so unlike a win rate it is meaningful at n=1 — which
 * is what makes it readable on a one-day view.
 */
export function disciplineScore(trades: readonly GaugeTrade[]): DisciplineScore {
  let posTotal = 0;
  let negTotal = 0;
  const grades: number[] = [];

  for (const t of trades) {
    const g = tradeDisciplineGrade(t);
    if (g == null) continue;
    const b = t.booleans ?? {};
    for (const k of DISCIPLINE_BREACH_KEYS) {
      if (b[k] === true) negTotal++; else posTotal++;
    }
    grades.push(g);
  }

  if (!grades.length) {
    return { value: null, arcPct: 0, sample: 0, posTotal, negTotal };
  }
  const avg = grades.reduce((s, g) => s + g, 0) / grades.length;
  const pct = (avg + 1) * 50;
  return { value: pct, arcPct: clamp100(pct), sample: grades.length, posTotal, negTotal };
}

// ---------------------------------------------------------------------------
// The equity series
// ---------------------------------------------------------------------------

export interface EquityInput {
  date: string;
  time: string;
  delta: number;
  /** The instrument, for the hover readout. Empty for an adjustment. */
  contract?: string;
  /**
   * What moved the line. Defaults to a trade.
   *
   * ⚠ The curve is no longer one-point-per-trade (#41): account adjustments are
   * interleaved as their own kind, so anything that COUNTS these points has to
   * ask. Labelling three points "3 trades" when one is a payout is a small lie
   * that a trader reconciling against the Trades tab will hit immediately.
   */
  kind?: 'trade' | 'adj';
  /** For an adjustment: which kind, e.g. `Payout`. Empty for a trade. */
  label?: string;
  note?: string;
}

export interface EquityPoint {
  date: string;
  equity: number;
  delta: number;
  kind: 'trade' | 'adj';
  label: string;
  note: string;
  /**
   * The two fields the hover readout needs (#47).
   *
   * ⚠ `time` WAS ALREADY ON THE INPUT and was being dropped here, so the
   * curve knew when every step happened and could not say. `contract` is new
   * on the input too. Both are carried, never derived — an adjustment has no
   * instrument and no clock time, and both are empty strings there rather
   * than a plausible-looking blank that reads as missing data.
   */
  time: string;
  contract: string;
}

/**
 * Cumulative equity, one point per trade, in chronological order.
 *
 * ⚠ SORTED BY `date` THEN `time`, not by whatever order the read returned. The
 * trades read is ordered by `id` for paging correctness, so an unsorted cumulate
 * produces a curve whose shape is arbitrary while its endpoint is right — which
 * looks like a chart and is not one.
 *
 * ✅ Account ADJUSTMENTS are folded in (#41). The caller interleaves them as
 * `kind: 'adj'` legs — see `domain/dashAdjustments` for which ones qualify — so
 * a curve on an account with a carry-in balance opens at that balance instead of
 * at zero, and a payout is a visible step rather than nothing at all.
 *
 * ⚠ THIS DOCSTRING USED TO SAY THE GAP WAS "named on screen until it lands."
 * It was not named anywhere on screen, and had not been for as long as the claim
 * existed. A comment asserting a user-visible behaviour is a claim like any
 * other, and this one was false in a file nobody had reason to re-read.
 *
 * ⚠ ON A TIE, THE ADJUSTMENT COMES FIRST — beta's rule (`a.kind === 'adj' ?
 * -1 : 1`). It matters at the open: a carry-in dated the same day as the first
 * trade must raise the line BEFORE that trade moves it, or the curve starts at
 * the trade's P&L and jumps. Adjustments carry no time, so string order already
 * puts them first; the explicit tie-break is here so a later change to the time
 * format cannot quietly reverse it.
 */
export function equitySeries(legs: readonly EquityInput[]): EquityPoint[] {
  const sorted = [...legs].sort((a, b) =>
    `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`)
    || ((a.kind === 'adj' ? 0 : 1) - (b.kind === 'adj' ? 0 : 1)));
  let running = 0;
  return sorted.map((l) => {
    running += l.delta;
    return {
      date: l.date, equity: running, delta: l.delta,
      kind: l.kind ?? 'trade', label: l.label ?? '', note: l.note ?? '',
      time: l.time ?? '', contract: l.contract ?? '',
    };
  });
}

/** The unit a gauge set was computed in — echoed so the UI cannot mislabel it. */
export type GaugeUnit = PnlUnit;
